/**
 * Icons.tsx - Reusable Icon Components
 * 
 * This file contains all the SVG icon components used throughout
 * the application. Centralizing icons makes them easy to modify
 * and ensures consistency in styling.
 */

import React from 'react';

interface IconBaseProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
  color?: string;
  strokeWidth?: number;
  className?: string;
}

// Base icon wrapper with common props
const IconBase: React.FC<IconBaseProps> = ({ 
  children, 
  size = 20, 
  color = 'currentColor', 
  strokeWidth = 2,
  className = '',
  ...props 
}) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {children}
  </svg>
);

// Navigation Icons
export const DashboardIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <rect x="3" y="3" width="7" height="7"/>
    <rect x="14" y="3" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/>
  </IconBase>
);

export const VendorsIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16l7-3 7 3z"/>
  </IconBase>
);

export const MenuIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </IconBase>
);

// Action Icons
export const PlusIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </IconBase>
);

export const EditIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </IconBase>
);

export const DeleteIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <polyline points="3,6 5,6 21,6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </IconBase>
);

export const CloseIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </IconBase>
);

// Arrow Icons
export const ArrowLeftIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12,19 5,12 12,5"/>
  </IconBase>
);

export const ArrowRightIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12,5 19,12 12,19"/>
  </IconBase>
);

// Status Icons
export const CheckIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <polyline points="20,6 9,17 4,12"/>
  </IconBase>
);

export const AlertIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <circle cx="12" cy="16" r="1"/>
  </IconBase>
);

// File Icons
export const UploadIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7,10 12,5 17,10"/>
    <line x1="12" y1="5" x2="12" y2="15"/>
  </IconBase>
);

export const DownloadIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7,10 12,15 17,10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </IconBase>
);

export const ImageIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21,15 16,10 5,21"/>
  </IconBase>
);

// Business Icons
export const DollarIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <line x1="12" y1="1" x2="12" y2="23"/>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </IconBase>
);

export const TrendingUpIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18"/>
    <polyline points="17,6 23,6 23,12"/>
  </IconBase>
);

export const ClockIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12,6 12,12 16,14"/>
  </IconBase>
);

export const CalendarIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </IconBase>
);

// Users Icon - Perfect for Users Management section
export const UsersIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </IconBase>
);

// Custom Stadium Icon
export const StadiumIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <ellipse cx="12" cy="12" rx="10" ry="6"/>
    <ellipse cx="12" cy="12" rx="6" ry="3"/>
    <circle cx="12" cy="12" r="1"/>
  </IconBase>
);

// Settings Icon
export const SettingsIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </IconBase>
);

// Account & Navigation Icons (Added for account system and orders)
export const UserIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </IconBase>
);

export const OrdersIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4"/>
    <line x1="8" y1="21" x2="8" y2="13"/>
    <line x1="12" y1="21" x2="12" y2="13"/>
    <line x1="16" y1="21" x2="16" y2="13"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </IconBase>
);

export const AnalyticsIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </IconBase>
);

// Events & Promotions Icon
export const EventsIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <rect x="3" y="4" width="18" height="16" rx="2"/>
    <path d="M16 2v4"/>
    <path d="M8 2v4"/>
    <path d="M3 10h18"/>
    <path d="M8 14h.01"/>
    <path d="M12 14h.01"/>
    <path d="M16 14h.01"/>
  </IconBase>
);

// Search and Filter Icons (for Users Management)
export const SearchIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <circle cx="11" cy="11" r="8"/>
    <path d="M21 21l-4.35-4.35"/>
  </IconBase>
);

export const FilterIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/>
  </IconBase>
);

export const SortIcon: React.FC<IconBaseProps> = (props) => (
  <IconBase {...props}>
    <path d="M3 6h18"/>
    <path d="M7 12h10"/>
    <path d="M10 18h4"/>
  </IconBase>
);

// Default export for convenience
const Icons = {
  DashboardIcon,
  VendorsIcon,
  MenuIcon,
  PlusIcon,
  EditIcon,
  DeleteIcon,
  CloseIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  AlertIcon,
  UploadIcon,
  DownloadIcon,
  ImageIcon,
  DollarIcon,
  TrendingUpIcon,
  ClockIcon,
  CalendarIcon,
  UsersIcon,
  StadiumIcon,
  SettingsIcon,
  UserIcon,
  OrdersIcon,
  AnalyticsIcon,
  EventsIcon,
  SearchIcon,
  FilterIcon,
  SortIcon
};

export default Icons;